import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import apiConfig from '../apiConfig';
import {
  clearCustomerAuthToken,
  getStoredCustomerAuthToken,
} from '../utils/auth/customerAuthToken';

interface RetryConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
  _maxRetries?: number;
}

const RETRY_DEFAULTS = {
  maxRetries: 3,
  baseDelay: 1_000,
  retryableMethods: new Set(['get', 'head', 'options', 'put', 'delete']),
};

function isRetryable(error: AxiosError): boolean {
  if (!error.config) return false;

  const method = (error.config.method ?? '').toLowerCase();
  if (!RETRY_DEFAULTS.retryableMethods.has(method)) return false;

  if (!error.response) return true;

  return error.response.status >= 500;
}

const api = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: 15_000,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getStoredCustomerAuthToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(undefined, async (error: AxiosError) => {
  if (error.response?.status === 401) {
    clearCustomerAuthToken();
  }

  const config = error.config as RetryConfig | undefined;
  if (!config || !isRetryable(error)) return Promise.reject(error);

  const maxRetries = config._maxRetries ?? RETRY_DEFAULTS.maxRetries;
  const retryCount = config._retryCount ?? 0;

  if (retryCount >= maxRetries) return Promise.reject(error);

  config._retryCount = retryCount + 1;

  const delay = RETRY_DEFAULTS.baseDelay * Math.pow(2, retryCount);
  await new Promise((r) => setTimeout(r, delay));

  return api.request(config);
});

export default api;
